import React, {useCallback, useEffect, useState} from 'react'
import {Box, Typography} from '@mui/material'
import moment from 'moment'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import FilePreviewDialog from 'src/components-new/feedback/FilePreviewDialog'
import getFileMIMEtype from 'src/components-new/feedback/getFileMIMEtype'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import {docTypesOptions, PREVIEW_MIME_TYPES, STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {DocRecord} from 'src/pages-new/documents/columns'

interface ActivityDocumentsTabNewProps {
  activityId: string
  beneficiaryId?: string
  /**
   * Adds the activity sheet (the PDF export of the activity, the same one the doctor
   * downloads from the patient's activities) as a default first document.
   */
  showActivitySheet?: boolean
  plannedDate?: string
  completionDate?: string
}

const INITIAL_VIEWER_STATE = {open: false, filePath: '', mime: '', fileName: '', status: STATUS.PENDING}

const ACTIVITY_SHEET_ROW_ID = 'activity-sheet'
const ACTIVITY_SHEET_LABEL = 'Diario digitale'
// Option added in code to this tab's "Categoria" filter only: in psp-config it would also be offered
// as a category for the uploaded documents
const ACTIVITY_SHEET_CATEGORY = 'activity-sheet'

// Name and category are both "Diario digitale"; its upload date is when the activity was completed,
// while the file name carries the activity's date like the doctor's activity sheet.
function buildActivitySheetRow(plannedDate?: string, completionDate?: string): DocRecord {
  const dateStr = plannedDate ? moment(plannedDate).format('DD-MM-YYYY') : ''
  return {
    _id: ACTIVITY_SHEET_ROW_ID,
    name: ACTIVITY_SHEET_LABEL,
    fileName: `Diario_digitale_${dateStr}.pdf`,
    categoryReadable: ACTIVITY_SHEET_LABEL,
    uploadDate: completionDate,
    type: docTypesOptions.find((t) => t.value === 'DOCUMENT_PDF')?.label,
  }
}

// The activity sheet isn't a stored document, so the server-side filters can't match it: apply them here.
function activitySheetMatchesFilters(filters: FilterResult[], completionDate?: string): boolean {
  const category = filters.find((f) => f.property === 'category')?.value
  if (category && category !== ACTIVITY_SHEET_CATEGORY) return false
  const [from, to] = (filters.find((f) => f.property === 'rangeDates')?.value ?? []) as (string | null)[]
  if (!from && !to) return true
  if (!completionDate) return false
  const date = moment(completionDate)
  return (!from || !date.isBefore(from)) && (!to || !date.isAfter(to))
}

function buildActivityDocumentColumns(
  onView: (row: DocRecord) => void,
  columnOptions?: {id: string; label: string}[]
): ColumnDef<DocRecord>[] {
  const actionsCol: ColumnDef<DocRecord> = {
    key: 'actions',
    header: '',
    width: 56,
    isActionColumn: true,
    render: (row) => (
      <Box onClick={(e) => e.stopPropagation()}>
        <ActionIconButton variant="download" tooltip="Scarica" onClick={() => onView(row)} />
      </Box>
    ),
  }

  const renderCell = (id: string) => (row: DocRecord): React.ReactNode => {
    if (id === 'uploadDate') {
      return <Typography variant="body2">{row.uploadDate ? moment(row.uploadDate).format('DD MMMM YYYY, HH:mm') : '—'}</Typography>
    }
    if (id === 'categoryReadable' || id === 'category') {
      return <Typography variant="body2">{row.categoryReadable ?? '—'}</Typography>
    }
    if (id === 'name') {
      return <Typography variant="body2">{row.name ?? row.fileName ?? '—'}</Typography>
    }
    return <Typography variant="body2">{(row as any)[id] ?? '—'}</Typography>
  }

  if (columnOptions && columnOptions.length > 0) {
    const extraCols: ColumnDef<DocRecord>[] = columnOptions.map((col) => ({
      key: col.id,
      header: col.label,
      hideInMobileCard: col.id === 'name',
      render: renderCell(col.id),
    }))
    return [...extraCols, actionsCol]
  }

  return [
    {key: 'categoryReadable', header: 'CATEGORIA', render: renderCell('categoryReadable')},
    {key: 'uploadDate', header: 'CARICATO IL', render: renderCell('uploadDate')},
    {key: 'name', header: 'NOME', hideInMobileCard: true, render: renderCell('name')},
    {key: 'type', header: 'TIPO', render: renderCell('type')},
    actionsCol,
  ]
}

/**
 * Read-only documents list for an activity — view/download only, no
 * upload/edit/delete (the whole ActivityDetailDialogNew is read-only).
 * Reuses the activity-scoped document endpoints (activityId in the URL),
 * so it can't reuse the generic useFilePreview hook, which always calls the
 * non-activity-scoped download endpoint.
 */
export default function ActivityDocumentsTabNew({
  activityId,
  beneficiaryId,
  showActivitySheet = false,
  plannedDate,
  completionDate,
}: ActivityDocumentsTabNewProps): JSX.Element {
  const documentsConfigurations = useAppSelector((state) => state.pspConfigReducer.data.documentsConfigurations)
  const activityDocumentsColumnOptions = useAppSelector(
    (state) => state.pspConfigReducer.data.patientDetailConfiguration?.activityDocumentsConfiguration?.columnOptions
  )
  const categoryOptions: {value: string; label: string}[] = documentsConfigurations?.categoryOptions?.categoriesFilters ?? []

  const [data, setData] = useState<DocRecord[]>([])
  const [count, setCount] = useState(0)
  const [loadStatus, setLoadStatus] = useState<string>(STATUS.PENDING)
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: '-uploadDate'})
  const [activeFilters, setActiveFilters] = useState<FilterResult[]>([])
  const [viewerState, setViewerState] = useState(INITIAL_VIEWER_STATE)
  const [statusDownload, setStatusDownload] = useState<string | undefined>()

  const parseCategoryToLabel = (value: string): string | undefined =>
    categoryOptions.find((c) => c.value === value)?.label

  const parseCategories = (category?: string[]): string =>
    category && category.length > 0 ? category.map((c) => parseCategoryToLabel(c) ?? c).join(', ') : '-'

  const fetchData = useCallback(async () => {
    // No stored document has the activity sheet's category: only the sheet itself matches
    if (activeFilters.find((f) => f.property === 'category')?.value === ACTIVITY_SHEET_CATEGORY) {
      setData([])
      setCount(0)
      setLoadStatus(STATUS.FULFILLED)
      return
    }
    setLoadStatus(STATUS.PENDING)
    const fetched: any[] = (await apiClient.getActivitiesDocuments(
      activityId, beneficiaryId ?? '', activeFilters, setLoadStatus, pagination
    )) ?? []
    const parsed: DocRecord[] = fetched.map((doc: any) => ({
      ...doc,
      categoryReadable: parseCategories(doc.category),
      type: docTypesOptions.find((t: any) => t.value === doc.type)?.label ?? doc.type,
    }))
    setData(parsed)
    const cnt = (await apiClient.getActivitiesDocumentsCount(activityId, activeFilters, () => {})) ?? 0
    setCount(Number(cnt))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityId, beneficiaryId, activeFilters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (statusDownload === STATUS.REJECTED) setViewerState(INITIAL_VIEWER_STATE)
  }, [statusDownload])

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setActiveFilters(values)
    setPagination((p) => ({...p, page: 0}))
  }

  const handleViewActivitySheet = async (row: DocRecord) => {
    const fileName = row.fileName ?? 'Diario_digitale.pdf'
    setViewerState({open: true, filePath: '', mime: 'application/pdf', fileName, status: STATUS.PENDING})
    const response = await apiClient.exportPatientActivity(beneficiaryId ?? '', activityId, setStatusDownload)
    if (!response) return
    const blob = new Blob([response.data], {type: 'application/pdf'})
    const url = (window.URL || window.webkitURL).createObjectURL(blob)
    setViewerState((prev) => ({...prev, filePath: url, status: STATUS.FULFILLED}))
  }

  const handleView = async (row: DocRecord) => {
    if (row._id === ACTIVITY_SHEET_ROW_ID) {
      await handleViewActivitySheet(row)
      return
    }
    if (!row.fileId) return
    const mime = getFileMIMEtype(row.fileName)
    const downloadName = row.name ?? row.fileName ?? 'documento'

    if (mime !== undefined && PREVIEW_MIME_TYPES.includes(mime)) {
      setViewerState({open: true, filePath: '', mime, fileName: downloadName, status: STATUS.PENDING})
      const fileResponse = await apiClient.activitiesDocumentDownload(activityId, row.fileId, setStatusDownload)
      const blob = new Blob([new Uint8Array(fileResponse)], {type: mime})
      const url = (window.URL || window.webkitURL).createObjectURL(blob)
      setViewerState((prev) => ({...prev, filePath: url, status: STATUS.FULFILLED}))
      return
    }

    const fileResponse = await apiClient.activitiesDocumentDownload(activityId, row.fileId, setStatusDownload)
    const blob = new Blob([new Uint8Array(fileResponse)], {type: ''})
    const url = (window.URL || window.webkitURL).createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = downloadName
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  }

  const columns = buildActivityDocumentColumns(handleView, activityDocumentsColumnOptions)

  const rows = showActivitySheet && pagination.page === 0 && activitySheetMatchesFilters(activeFilters, completionDate)
    ? [buildActivitySheetRow(plannedDate, completionDate), ...data]
    : data

  const filterConfig: FilterItem[] = [
    {
      id: 'category',
      label: 'Categoria',
      type: 'select',
      options: showActivitySheet
        ? [...categoryOptions, {value: ACTIVITY_SHEET_CATEGORY, label: ACTIVITY_SHEET_LABEL}]
        : categoryOptions,
    },
    {id: 'rangeDates', label: 'Data inserimento', type: 'rangePicker'},
  ]

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
        <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />
      </Box>

      {loadStatus === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setLoadStatus(STATUS.FULFILLED)} sx={{mb: 2}}>
          {'Errore nel recuperare i documenti.'}
        </InlineMessage>
      )}

      <DataTable<DocRecord>
        columns={columns}
        rows={rows}
        keyField="_id"
        loading={loadStatus === STATUS.PENDING}
        emptyMessage="Nessun documento trovato"
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
        onRowClick={handleView}
        mobileCardTitle={(row) => row.name ?? row.fileName ?? '—'}
      />

      <FilePreviewDialog
        fileName={viewerState.fileName}
        filePath={viewerState.filePath}
        mime={viewerState.mime}
        onClose={() => {
          if (viewerState.filePath) URL.revokeObjectURL(viewerState.filePath)
          setViewerState(INITIAL_VIEWER_STATE)
        }}
        open={viewerState.open}
        status={viewerState.status}
      />
    </Box>
  )
}
